// frontend/src/components/Portfolio/TimeBasedProjections.tsx
// Updated with scientifically-vetted IPCC AR6 scenarios and custom scenario support

import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Speed as SpeedIcon,
  InfoOutlined as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';

interface PortfolioItem {
  postal_code?: string;
  zip_code?: string;
  city?: string;
  state?: string;
  risk_score?: number;
  total_damage_10?: number;
  total_event_count_10?: number;
  asset_value?: number | string;
  [key: string]: any;
}

interface TimeBasedProjectionsProps {
  portfolio: {
    items: PortfolioItem[];
    [key: string]: any;
  };
}

interface ClimateScenario {
  name: string;
  description: string;
  source: string;
  baseline: number;
  year2: number;
  year4: number;
  year6: number;
  year8: number;
  year10: number;
  ssp?: string;
  warming?: string;
}

/**
 * IPCC AR6 Climate Scenarios
 * Based on Shared Socioeconomic Pathways (SSPs) from IPCC Sixth Assessment Report
 * Sources: IPCC AR6 WGI (2021), Kotz et al. (2024)
 */
const CLIMATE_SCENARIOS: { [key: string]: ClimateScenario } = {
  conservative: {
    name: 'Conservative (Low Emissions)',
    description: 'Based on IPCC AR6 SSP1-2.6 scenario with strong mitigation. Assumes rapid emissions reductions and global cooperation. Temperature increase limited to ~2°C by 2100.',
    source: 'IPCC AR6 WGI (2021) - SSP1-2.6',
    ssp: 'SSP1-2.6',
    warming: '~1.5-2.0°C by 2100',
    baseline: 1.0,
    year2: 1.03,
    year4: 1.06,
    year6: 1.10,
    year8: 1.15,
    year10: 1.20,
  },
  moderate: {
    name: 'Moderate (Middle-of-the-Road)',
    description: 'Based on IPCC AR6 SSP2-4.5 scenario. Current policy trajectory with moderate climate action. Most likely scenario based on International Energy Agency current policies.',
    source: 'IPCC AR6 WGI (2021) - SSP2-4.5',
    ssp: 'SSP2-4.5',
    warming: '~2.5-3.0°C by 2100',
    baseline: 1.0,
    year2: 1.05,
    year4: 1.12,
    year6: 1.20,
    year8: 1.30,
    year10: 1.40,
  },
  severe: {
    name: 'Severe (High Emissions)',
    description: 'Based on IPCC AR6 SSP5-8.5 scenario. High emissions with limited mitigation. Worst-case but plausible scenario for risk management and stress testing purposes.',
    source: 'IPCC AR6 WGI (2021) - SSP5-8.5',
    ssp: 'SSP5-8.5',
    warming: '~4.0-5.0°C by 2100',
    baseline: 1.0,
    year2: 1.08,
    year4: 1.18,
    year6: 1.30,
    year8: 1.45,
    year10: 1.62,
  },
};

export default function TimeBasedProjections({ portfolio }: TimeBasedProjectionsProps) {
  const [selectedScenario, setSelectedScenario] = useState<string>('moderate');
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [methodologyOpen, setMethodologyOpen] = useState(false);
  const [customScenario, setCustomScenario] = useState<ClimateScenario>({
    name: 'Custom Scenario',
    description: 'User-defined projection factors',
    source: 'User Input',
    baseline: 1.0,
    year2: 1.05,
    year4: 1.12,
    year6: 1.20,
    year8: 1.30,
    year10: 1.40,
  });

  const currentScenario = selectedScenario === 'custom' 
    ? customScenario 
    : CLIMATE_SCENARIOS[selectedScenario];

  const parseNumeric = (value: any): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value.replace(/[$,]/g, ''));
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const projections = useMemo(() => {
    const items = portfolio.items || [];
    
    return items.map(item => {
      const baseDamage = parseFloat(String(item.total_damage_10 || 0));
      const baseEvents = parseFloat(String(item.total_event_count_10 || 0));
      const riskScore = parseFloat(String(item.risk_score || 5));
      
      const yearlyProjections = Array.from({ length: 11 }, (_, year) => {
        const accelerationKey = year === 0 ? 'baseline' : 
                                year <= 2 ? 'year2' :
                                year <= 4 ? 'year4' :
                                year <= 6 ? 'year6' :
                                year <= 8 ? 'year8' : 'year10';
        
        const factor = currentScenario[accelerationKey as keyof ClimateScenario] as number;
        const annualDamage = (baseDamage / 10) * factor;
        const annualEvents = (baseEvents / 10) * factor;
        
        return {
          year,
          damage: annualDamage,
          events: annualEvents,
          cumulativeDamage: Array.from({ length: year + 1 }, (_, y) => {
            const yKey = y === 0 ? 'baseline' : 
                        y <= 2 ? 'year2' :
                        y <= 4 ? 'year4' :
                        y <= 6 ? 'year6' :
                        y <= 8 ? 'year8' : 'year10';
            return (baseDamage / 10) * (currentScenario[yKey as keyof ClimateScenario] as number);
          }).reduce((a, b) => a + b, 0),
        };
      });

      const totalProjectedDamage = yearlyProjections.reduce((sum, y) => sum + y.damage, 0);
      const year0Damage = yearlyProjections[0].damage;
      const year10Damage = yearlyProjections[10].damage;
      const accelerationRate = ((year10Damage - year0Damage) / year0Damage) * 100;

      return {
        ...item,
        yearlyProjections,
        totalProjectedDamage,
        accelerationRate,
        trend: riskScore > 6 ? 'Escalating' : riskScore > 3 ? 'Moderate' : 'Stable',
      };
    });
  }, [portfolio.items, currentScenario]);

  const portfolioProjections = useMemo(() => {
    const years = Array.from({ length: 11 }, (_, i) => i);
    
    return years.map(year => {
      const yearData = projections.reduce((acc, item) => {
        const yearProjection = item.yearlyProjections[year];
        return {
          year,
          totalDamage: acc.totalDamage + yearProjection.damage,
          totalEvents: acc.totalEvents + yearProjection.events,
          cumulativeDamage: acc.cumulativeDamage + yearProjection.cumulativeDamage,
        };
      }, { year, totalDamage: 0, totalEvents: 0, cumulativeDamage: 0 });

      return yearData;
    });
  }, [projections]);

  const portfolioStats = useMemo(() => {
    const totalAssetValue = projections.reduce((sum, item) => 
      sum + parseNumeric(item.asset_value), 0
    );
    
    const total10YearDamage = projections.reduce((sum, item) => 
      sum + item.totalProjectedDamage, 0
    );
    
    const avgAcceleration = projections.reduce((sum, item) => 
      sum + item.accelerationRate, 0
    ) / Math.max(projections.length, 1);

    const highRiskLocations = projections.filter(item => 
      (item.risk_score || 0) > 6
    ).length;

    return {
      totalAssetValue,
      total10YearDamage,
      avgAcceleration,
      riskExposure: (total10YearDamage / totalAssetValue) * 100,
      highRiskLocations,
      peakYearDamage: portfolioProjections[10]?.totalDamage || 0,
    };
  }, [projections, portfolioProjections]);

  const formatCurrency = (value: number): string => {
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const getTrendColor = (trend: string): string => {
    switch (trend) {
      case 'Escalating': return '#d32f2f';
      case 'Moderate': return '#f57c00';
      case 'Stable': return '#388e3c';
      default: return '#757575';
    }
  };

  const handleCustomScenarioSave = () => {
    setSelectedScenario('custom');
    setCustomDialogOpen(false);
  };

  const scenarioIncreasePercent = ((currentScenario.year10 - 1) * 100).toFixed(0);

  return (
    <Box sx={{ 
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      p: 3
    }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
            Time-Based Climate Risk Projections
          </Typography>
          <Typography variant="body2" sx={{ color: '#666666' }}>
            10-year damage projections using IPCC AR6 climate scenarios
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<InfoIcon />}
          onClick={() => setMethodologyOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          Methodology
        </Button>
      </Box>

      {/* Scenario Selection */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <FormControl fullWidth>
                <InputLabel>Climate Scenario</InputLabel>
                <Select
                  value={selectedScenario}
                  onChange={(e) => setSelectedScenario(e.target.value)}
                  label="Climate Scenario"
                >
                  {Object.keys(CLIMATE_SCENARIOS).map(key => (
                    <MenuItem key={key} value={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{CLIMATE_SCENARIOS[key].name}</span>
                        <Tooltip title={CLIMATE_SCENARIOS[key].description} arrow>
                          <InfoIcon sx={{ fontSize: 18, color: '#666' }} />
                        </Tooltip>
                      </Box>
                    </MenuItem>
                  ))}
                  <MenuItem value="custom">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Custom Scenario</span>
                      <Tooltip title="Define your own acceleration factors based on specific research or organizational requirements" arrow>
                        <InfoIcon sx={{ fontSize: 18, color: '#666' }} />
                      </Tooltip>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                fullWidth
                onClick={() => setCustomDialogOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Create Custom Scenario
              </Button>
            </Grid>
          </Grid>

          {/* Scenario Details */}
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1 }}>
              {currentScenario.name}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
              {currentScenario.description}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" sx={{ color: '#666666' }}>
                  <strong>Source:</strong> {currentScenario.source}
                </Typography>
              </Grid>
              {currentScenario.ssp && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ color: '#666666' }}>
                    <strong>SSP Pathway:</strong> {currentScenario.ssp}
                  </Typography>
                </Grid>
              )}
              {currentScenario.warming && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ color: '#666666' }}>
                    <strong>Projected Warming:</strong> {currentScenario.warming}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Alert about projections */}
      <Alert 
        severity="info" 
        sx={{ 
          mb: 3,
          backgroundColor: '#e3f2fd',
          border: '1px solid #90caf9',
          color: '#1a1a1a',
          '& .MuiAlert-icon': { color: '#1976d2' }
        }}
      >
        Projections use {currentScenario.name} scenario showing {scenarioIncreasePercent}% increase in climate impacts by year 10. 
        Baseline represents current conditions based on historical 10-year event data.
      </Alert>

      {/* Portfolio-Wide Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Tooltip title="Total projected cumulative damage across all portfolio locations over 10 years" arrow>
            <Card sx={{ 
              backgroundColor: '#ffebee', 
              border: '1px solid #ef9a9a',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              height: '100%',
              cursor: 'help'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <WarningIcon sx={{ color: '#c62828', mr: 1 }} />
                  <Typography variant="caption" sx={{ color: '#666666', fontWeight: 500 }}>
                    Total 10-Year Risk
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ color: '#c62828', fontWeight: 600 }}>
                  {formatCurrency(portfolioStats.total10YearDamage)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666666' }}>
                  Projected cumulative damage
                </Typography>
              </CardContent>
            </Card>
          </Tooltip>
        </Grid>

        <Grid item xs={12} md={3}>
          <Tooltip title="Average rate of increase in climate risk from year 0 to year 10 across portfolio" arrow>
            <Card sx={{ 
              backgroundColor: '#fff3e0', 
              border: '1px solid #ffcc80',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              height: '100%',
              cursor: 'help'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SpeedIcon sx={{ color: '#ef6c00', mr: 1 }} />
                  <Typography variant="caption" sx={{ color: '#666666', fontWeight: 500 }}>
                    Acceleration Rate
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ color: '#ef6c00', fontWeight: 600 }}>
                  +{portfolioStats.avgAcceleration.toFixed(1)}%
                </Typography>
                <Typography variant="caption" sx={{ color: '#666666' }}>
                  Average annual increase
                </Typography>
              </CardContent>
            </Card>
          </Tooltip>
        </Grid>

        <Grid item xs={12} md={3}>
          <Tooltip title="Expected annual damage in the final year (year 10) of projection period" arrow>
            <Card sx={{ 
              backgroundColor: '#e3f2fd', 
              border: '1px solid #90caf9',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              height: '100%',
              cursor: 'help'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ScheduleIcon sx={{ color: '#1565c0', mr: 1 }} />
                  <Typography variant="caption" sx={{ color: '#666666', fontWeight: 500 }}>
                    Peak Year Damage
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ color: '#1565c0', fontWeight: 600 }}>
                  {formatCurrency(portfolioStats.peakYearDamage)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666666' }}>
                  Year 10 annual damage
                </Typography>
              </CardContent>
            </Card>
          </Tooltip>
        </Grid>

        <Grid item xs={12} md={3}>
          <Tooltip title="Total 10-year projected damage as a percentage of total portfolio asset value" arrow>
            <Card sx={{ 
              backgroundColor: '#fce4ec', 
              border: '1px solid #f8bbd0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              height: '100%',
              cursor: 'help'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUpIcon sx={{ color: '#c2185b', mr: 1 }} />
                  <Typography variant="caption" sx={{ color: '#666666', fontWeight: 500 }}>
                    Risk Exposure
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ color: '#c2185b', fontWeight: 600 }}>
                  {portfolioStats.riskExposure.toFixed(1)}%
                </Typography>
                <Typography variant="caption" sx={{ color: '#666666' }}>
                  Of total asset value
                </Typography>
              </CardContent>
            </Card>
          </Tooltip>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#1a1a1a', mb: 2, fontWeight: 600 }}>
                Annual Damage Projection
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={portfolioProjections}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis 
                    dataKey="year" 
                    label={{ value: 'Year', position: 'insideBottom', offset: -5 }}
                    stroke="#666"
                  />
                  <YAxis 
                    label={{ value: 'Damage ($)', angle: -90, position: 'insideLeft' }}
                    stroke="#666"
                    tickFormatter={(value) => formatCurrency(value)}
                  />
                  <RechartsTooltip 
                    formatter={(value: any) => formatCurrency(value)}
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                  />
                  <Legend />
                  <Bar dataKey="totalDamage" fill="#ef5350" name="Annual Damage" />
                  <Line 
                    type="monotone" 
                    dataKey="cumulativeDamage" 
                    stroke="#1976d2" 
                    strokeWidth={2}
                    name="Cumulative Damage"
                    dot={{ fill: '#1976d2' }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#1a1a1a', mb: 2, fontWeight: 600 }}>
                Event Frequency Projection
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={portfolioProjections}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis 
                    dataKey="year" 
                    label={{ value: 'Year', position: 'insideBottom', offset: -5 }}
                    stroke="#666"
                  />
                  <YAxis 
                    label={{ value: 'Events', angle: -90, position: 'insideLeft' }}
                    stroke="#666"
                  />
                  <RechartsTooltip 
                    formatter={(value: any) => Math.round(value)}
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                  />
                  <Legend />
                  <Bar dataKey="totalEvents" fill="#42a5f5" name="Climate Events" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Location-Specific Projections Table */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#1a1a1a', mb: 2, fontWeight: 600 }}>
            Top 10 High-Risk Locations
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ color: '#1a1a1a', fontWeight: 600 }}>Location</TableCell>
                  <TableCell sx={{ color: '#1a1a1a', fontWeight: 600 }}>
                    <Tooltip title="Current annual damage based on historical data" arrow>
                      <span>Baseline Annual</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ color: '#1a1a1a', fontWeight: 600 }}>
                    <Tooltip title="Projected annual damage in year 10" arrow>
                      <span>Year 10 Annual</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ color: '#1a1a1a', fontWeight: 600 }}>
                    <Tooltip title="Total cumulative damage over 10-year period" arrow>
                      <span>Total 10-Year</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ color: '#1a1a1a', fontWeight: 600 }}>
                    <Tooltip title="Percentage increase from baseline to year 10" arrow>
                      <span>Acceleration</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ color: '#1a1a1a', fontWeight: 600 }}>Trend</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projections
                  .sort((a, b) => b.totalProjectedDamage - a.totalProjectedDamage)
                  .slice(0, 10)
                  .map((item, index) => (
                    <TableRow 
                     key={`${item.postal_code || item.zip_code}-${index}`}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f9f9f9' },
                        borderBottom: '1px solid #e0e0e0'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a1a1a' }}>
                          {item.city || 'Unknown'}, {item.state || ''}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666666' }}>
                          {item.postal_code || item.zip_code}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: '#1a1a1a', fontWeight: 500 }}>
                        {formatCurrency(item.yearlyProjections[0].damage)}
                      </TableCell>
                      <TableCell sx={{ color: '#1a1a1a', fontWeight: 500 }}>
                        {formatCurrency(item.yearlyProjections[10].damage)}
                      </TableCell>
                      <TableCell sx={{ color: '#1a1a1a', fontWeight: 600 }}>
                        {formatCurrency(item.totalProjectedDamage)}
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: item.accelerationRate > 50 ? '#d32f2f' : '#f57c00',
                            fontWeight: 600
                          }}
                        >
                          +{item.accelerationRate.toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.trend}
                          size="small"
                          sx={{
                            backgroundColor: `${getTrendColor(item.trend)}15`,
                            color: getTrendColor(item.trend),
                            fontWeight: 500,
                            border: `1px solid ${getTrendColor(item.trend)}60`
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Custom Scenario Dialog */}
      <Dialog 
        open={customDialogOpen} 
        onClose={() => setCustomDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Custom Climate Scenario</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Scenario Name"
              value={customScenario.name}
              onChange={(e) => setCustomScenario({...customScenario, name: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={customScenario.description}
              onChange={(e) => setCustomScenario({...customScenario, description: e.target.value})}
              multiline
              rows={2}
              sx={{ mb: 2 }}
              helperText="Explain the basis for your custom scenario (e.g., specific research, regional factors)"
            />
            <TextField
              fullWidth
              label="Data Source / Reference"
              value={customScenario.source}
              onChange={(e) => setCustomScenario({...customScenario, source: e.target.value})}
              sx={{ mb: 3 }}
              helperText="Cite the scientific paper, report, or methodology used"
            />
            
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Acceleration Factors (multipliers relative to baseline)
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4}>
                <Tooltip title="Risk multiplier for years 1-2. Example: 1.05 = 5% increase" arrow>
                  <TextField
                    fullWidth
                    label="Year 2"
                    type="number"
                    inputProps={{ step: 0.01, min: 1.0, max: 3.0 }}
                    value={customScenario.year2}
                    onChange={(e) => setCustomScenario({...customScenario, year2: parseFloat(e.target.value)})}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Tooltip title="Risk multiplier for years 3-4" arrow>
                  <TextField
                    fullWidth
                    label="Year 4"
                    type="number"
                    inputProps={{ step: 0.01, min: 1.0, max: 3.0 }}
                    value={customScenario.year4}
                    onChange={(e) => setCustomScenario({...customScenario, year4: parseFloat(e.target.value)})}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Tooltip title="Risk multiplier for years 5-6" arrow>
                  <TextField
                    fullWidth
                    label="Year 6"
                    type="number"
                    inputProps={{ step: 0.01, min: 1.0, max: 3.0 }}
                    value={customScenario.year6}
                    onChange={(e) => setCustomScenario({...customScenario, year6: parseFloat(e.target.value)})}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Tooltip title="Risk multiplier for years 7-8" arrow>
                  <TextField
                    fullWidth
                    label="Year 8"
                    type="number"
                    inputProps={{ step: 0.01, min: 1.0, max: 3.0 }}
                    value={customScenario.year8}
                    onChange={(e) => setCustomScenario({...customScenario, year8: parseFloat(e.target.value)})}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Tooltip title="Risk multiplier for years 9-10" arrow>
                  <TextField
                    fullWidth
                    label="Year 10"
                    type="number"
                    inputProps={{ step: 0.01, min: 1.0, max: 3.0 }}
                    value={customScenario.year10}
                    onChange={(e) => setCustomScenario({...customScenario, year10: parseFloat(e.target.value)})}
                  />
                </Tooltip>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 3 }}>
              Factors represent the multiplier applied to baseline risk. For example, 1.20 means a 20% increase from current conditions. 
              Values should increase over time to reflect climate acceleration.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCustomScenarioSave} variant="contained">
            Apply Custom Scenario
          </Button>
        </DialogActions>
      </Dialog>

      {/* Methodology Dialog */}
      <Dialog 
        open={methodologyOpen} 
        onClose={() => setMethodologyOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Projection Methodology & Scientific Basis
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Climate Scenarios & Data Sources
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  This tool uses climate scenarios from the IPCC Sixth Assessment Report (AR6), published by Working Group I in 2021. 
                  These scenarios represent different possible futures based on varying levels of greenhouse gas emissions and climate policy actions.
                </Typography>
                <Typography variant="body2" component="div">
                  <strong>Key Sources:</strong>
                  <ul>
                    <li>IPCC AR6 WGI (2021) - Physical Science Basis</li>
                    <li>IPCC AR6 WGII (2022) - Impacts, Adaptation and Vulnerability</li>
                    <li>Shared Socioeconomic Pathways (SSPs) - IPCC scenario framework</li>
                    <li>Kotz et al. (2024) - "The economic commitment of climate change" (Nature)</li>
                    <li>Network for Greening the Financial System (NGFS) Climate Scenarios</li>
                  </ul>
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Baseline Data
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  Projections use historical 10-year event data as the baseline, representing current climate conditions. 
                  This data includes observed climate-related events and associated damages at the ZIP code level.
                </Typography>
                <Typography variant="body2" paragraph>
                  The baseline assumes current atmospheric conditions and historical climate patterns. 
                  Future projections apply acceleration factors to account for expected changes in climate hazard frequency and intensity.
                </Typography>
                <Typography variant="body2">
                  <strong>Baseline data includes:</strong>
                  <ul>
                    <li>Historical event counts (floods, wildfires, storms, etc.)</li>
                    <li>CPI-adjusted economic damage estimates</li>
                    <li>Geographic and property-specific risk factors</li>
                  </ul>
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Acceleration Methodology
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  Climate acceleration factors represent the expected increase in climate risk over time due to:
                </Typography>
                <Typography variant="body2" component="div">
                  <ul>
                    <li>Rising global temperatures and associated hazard intensification</li>
                    <li>Increased frequency of extreme weather events</li>
                    <li>Changes in precipitation patterns and variability</li>
                    <li>Compound and cascading climate impacts</li>
                    <li>Non-linear climate system responses</li>
                  </ul>
                </Typography>
                <Typography variant="body2" paragraph sx={{ mt: 2 }}>
                  The methodology accounts for both immediate impacts and persistent effects up to 10 years, 
                  consistent with recent research on climate damage persistence (Kotz et al., 2024). This approach 
                  recognizes that climate shocks can have lasting economic effects beyond the year of occurrence.
                </Typography>
                <Typography variant="body2">
                  <strong>Note:</strong> Acceleration factors are applied uniformly across hazard types within each scenario. 
                  Actual impacts may vary by specific hazard, geographic location, and local adaptation measures.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Scenario Descriptions
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" component="div" sx={{ mb: 2 }}>
                  <strong>Conservative (SSP1-2.6):</strong> Strong climate action with rapid emission reductions. 
                  Global warming limited to 1.5-2.0°C by 2100. Represents best-case scenario with international cooperation, 
                  aggressive decarbonization, and achievement of net-zero emissions by 2050.
                </Typography>
                <Typography variant="body2" component="div" sx={{ mb: 2 }}>
                  <strong>Moderate (SSP2-4.5):</strong> Current policy trajectory with moderate climate action. 
                  Most likely scenario based on International Energy Agency projections and existing national commitments. 
                  Warming of 2.5-3.0°C by 2100. Represents a "middle-of-the-road" future with gradual emissions reductions.
                </Typography>
                <Typography variant="body2" component="div">
                  <strong>Severe (SSP5-8.5):</strong> High emissions with limited mitigation efforts. 
                  Warming of 4.0-5.0°C by 2100. Used for stress testing and worst-case risk management planning. 
                  While considered less likely than moderate scenarios, it represents an important upper bound for risk assessment.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Limitations & Uncertainties
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" component="div">
                  <strong>Users should be aware of the following limitations:</strong>
                  <ul>
                    <li><strong>Model Uncertainty:</strong> Projections are based on modeled scenarios and contain inherent uncertainty in climate system responses</li>
                    <li><strong>Policy Dependence:</strong> Actual outcomes depend on future emissions, policy actions, and technological developments</li>
                    <li><strong>Local Variability:</strong> Regional and local conditions may vary significantly from portfolio-level projections</li>
                    <li><strong>Tipping Points:</strong> Does not explicitly model climate tipping points or abrupt changes in the climate system</li>
                    <li><strong>Non-Market Impacts:</strong> Economic impacts may be underestimated as they don't include all non-market effects (biodiversity loss, cultural impacts, etc.)</li>
                    <li><strong>Adaptation:</strong> Projections assume limited adaptation; actual impacts may be reduced through mitigation and adaptation measures</li>
                    <li><strong>Time Horizon:</strong> 10-year timeframe is near-term; longer-term risks (2050-2100) may be substantially higher</li>
                    <li><strong>Compounding Effects:</strong> May not fully capture compound risks from multiple simultaneous hazards</li>
                  </ul>
                </Typography>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  These projections are for risk assessment and planning purposes. They should not be interpreted as precise predictions 
                  but rather as plausible future scenarios to inform decision-making.
                </Alert>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  References & Further Reading
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" component="div" sx={{ fontSize: '0.875rem' }}>
                  <ol>
                    <li>IPCC (2021). <em>Climate Change 2021: The Physical Science Basis</em>. Contribution of Working Group I to the Sixth Assessment Report. 
                    Available at: https://www.ipcc.ch/report/ar6/wg1/</li>
                    
                    <li>IPCC (2022). <em>Climate Change 2022: Impacts, Adaptation and Vulnerability</em>. Contribution of Working Group II to the Sixth Assessment Report. 
                    Available at: https://www.ipcc.ch/report/ar6/wg2/</li>
                    
                    <li>Kotz, M., Levermann, A., & Wenz, L. (2024). The economic commitment of climate change. <em>Nature</em>, 624, 551-557. 
                    DOI: 10.1038/s41586-023-06885-8</li>
                    
                    <li>Network for Greening the Financial System (NGFS). (2023). <em>NGFS Climate Scenarios for central banks and supervisors</em>. 
                    Available at: https://www.ngfs.net/en/ngfs-climate-scenarios</li>
                    
                    <li>Burke, M., Hsiang, S. M., & Miguel, E. (2015). Global non-linear effect of temperature on economic production. <em>Nature</em>, 527, 235-239.</li>
                    
                    <li>Diffenbaugh, N. S., & Burke, M. (2019). Global warming has increased global economic inequality. <em>Proceedings of the National Academy of Sciences</em>, 116(20), 9808-9813.</li>
                    
                    <li>Carleton, T., et al. (2022). Valuing the Global Mortality Consequences of Climate Change Accounting for Adaptation Costs and Benefits. <em>The Quarterly Journal of Economics</em>, 137(4), 2037-2105.</li>
                  </ol>
                </Typography>
                <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                  For questions about methodology or to report issues, please consult with climate risk professionals 
                  and review the original source materials cited above.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMethodologyOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}